import { db } from './db';
import {
  deleteDocument,
  getDocument,
  listAllDocuments,
  purgeDocument,
  putRemoteDocument,
} from './library';
import { captureSnapshot } from './snapshots';
import type { LocalSync } from './syncEngine';
import type { SyncedState } from './syncDecide';

/**
 * The library as the sync engine sees it: reads and writes through the ordinary
 * library functions, so the search index, the metadata projection and the link
 * index follow a change that arrived from another device exactly as they follow
 * one made here.
 *
 * `syncstate` holds what each document last agreed on with the cloud. It is
 * derived data: deleting it costs one extra round of comparison, nothing more.
 */
export const dexieLocalSync: LocalSync = {
  get: (id) => getDocument(id),
  listAll: () => listAllDocuments(),
  apply: (doc) => putRemoteDocument(doc),
  purge: (id) => purgeDocument(id),
  async preserve(doc) {
    await captureSnapshot(doc, 'remote');
  },
  agreed: (id) => db.syncstate.get(id),
  async setAgreed(state) {
    await db.syncstate.put(state);
  },
  listAgreed: () => db.syncstate.toArray(),
};

/** Forget every agreement (sign-out, or a fresh start after an account change). */
export async function clearSyncState(): Promise<void> {
  await db.syncstate.clear();
}

/** Move a document to the trash, for the paths that soft-delete rather than purge. */
export const softDelete = deleteDocument;

export type { SyncedState };
